import React, { createContext, useContext, useMemo, useState, useEffect, useRef, useCallback } from 'react';

type ShamblePlayableDriveMap = Record<string, Record<number, string[]>>;

interface DataContextType {
    eventData: any;
    setEventData: (eventData: any) => void;
    courseData: any;
    setCourseData: (courseData: any) => void;
    scoreData: any;
    setScoreData: (scoreData: any) => void;
    ownEventScore: any;
    setOwnEventScore: (ownEventScore: any) => void;
    scoreDataRef: any;
    registrationData: any;
    setRegistrationData: (registrationData: any) => void;
    leaderboardData: any;
    setLeaderboardData: (leaderboardData: any) => void;
    scoreCode: string;
    leaderboardCourseData: any;
    setLeaderboardCourseData: (leaderboardCourseData: any) => void;
    setScoreCode: (scoreCode: string) => void;
    currentRound: number;
    setCurrentRound: (currentRound: number) => void;
    startHole: number;
    setStartHole: (startHole: number) => void;
    leaderboardLastQuery: Date;
    setLeaderboardLastQuery: (leaderboardLastQuery: Date) => void;
    groupConfirmResult: boolean;
    setGroupConfirmResult: (groupConfirmResult: boolean) => void;
    groupScorer: boolean;
    setGroupScorer: (groupScorer: boolean) => void;
    userData: any;
    setUserData: (userData: any) => void;
    adminMode: boolean;
    setAdminMode: (adminMode: boolean) => void;
    adminEvents: any;
    setAdminEvents: (adminEvents: any) => void;
    shamblePlayableDriveMap: ShamblePlayableDriveMap;
    shambleMinTeamSize: number;
    hasUnevenShambleTeams: boolean;
    isShambleInPlay: (scoreCode: string, holeNumber: number) => boolean;
}

const DataContext = createContext<DataContextType | null>(null);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [eventData, setEventData] = useState<any>({});
    const [courseData, setCourseData] = useState<any>({});
    const [scoreData, setScoreData] = useState<any[]>([]);
    const [ownEventScore, setOwnEventScore] = useState<any>(null);
    const [registrationData, setRegistrationData] = useState<any[]>([]);
    const [leaderboardData, setLeaderboardData] = useState<any[]>([]);
    const [leaderboardCourseData, setLeaderboardCourseData] = useState<any[]>([]);
    const [scoreCode, setScoreCode] = useState('');
    const [startHole, setStartHole] = useState(0);
    const [currentRound, setCurrentRound] = useState(0);
    const [leaderboardLastQuery, setLeaderboardLastQuery] = useState(new Date('1/1/2000'));
    const [groupConfirmResult, setGroupConfirmResult] = useState(false);  
    const [groupScorer, setGroupScorer] = useState(false);
    const [adminMode, setAdminMode] = useState(false);
    const [userData, setUserData] = useState<any>({});
    const [adminEvents, setAdminEvents] = useState<any[]>([]);
    const [shamblePlayableDriveMap, setShamblePlayableDriveMap] = useState<ShamblePlayableDriveMap>({});
    const [shambleMinTeamSize, setShambleMinTeamSize] = useState(0);
    const [hasUnevenShambleTeams, setHasUnevenShambleTeams] = useState(false);
    const scoreDataRef = useRef<any>(null);

    useEffect(() => {
        const normalizedPlayFormat = (eventData?.playFormat || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
        const isShambleFormat = normalizedPlayFormat === 'shamble';
        const usesTeams = Boolean(eventData?.useTeams);

        if (!isShambleFormat || !usesTeams) {
            setShamblePlayableDriveMap({});
            setShambleMinTeamSize(0);
            setHasUnevenShambleTeams(false);
            return;
        }

        // Keep one entry per player registration; do not dedupe by scoreCode
        // because team members may intentionally share a scoreCode in team events.
        const reducedRegistrations = (registrationData || []).filter((registration: any) =>
            registration?.teamName && registration?.scoreCode
        );

        // Current team size source should follow reduced registrations used in scoring view.
        const reducedRegistrationBlocks = reducedRegistrations.reduce((acc: any[], registration: any) => {
            if (!acc.some((item: any) => item.scoreCode === registration.scoreCode)) {
                acc.push(registration);
            }
            return acc;
        }, []);

        const teamBuckets = reducedRegistrationBlocks
            .reduce((acc: Record<string, any[]>, registration: any) => {
                const teamName = String(registration.teamName);
                if (!acc[teamName]) acc[teamName] = [];
                acc[teamName].push(registration);
                return acc;
            }, {});

        const teams = Object.keys(teamBuckets);
        if (teams.length === 0) {
            setShamblePlayableDriveMap({});
            setShambleMinTeamSize(0);
            setHasUnevenShambleTeams(false);
            return;
        }

        const currentTeamSizes = teams.map((teamName) => teamBuckets[teamName].length);
        const configuredTeamSize = Number(eventData?.teamSize) || 0;
        const playableCount = configuredTeamSize > 0 ? Math.max(1, Math.floor(configuredTeamSize)) : 0;
        const unevenTeamSizes = currentTeamSizes.some((size) => size > playableCount);
        const minTeamSize = playableCount;

        setShambleMinTeamSize(minTeamSize);
        setHasUnevenShambleTeams(unevenTeamSizes);

        if (!unevenTeamSizes || minTeamSize <= 0) {
            setShamblePlayableDriveMap({});
            return;
        }

        const totalHoles = Number(eventData?.totalHoles) || Number(eventData?.rounds?.[0]?.totalHoles) || 18;
        const newPlayableMap: ShamblePlayableDriveMap = {};

        teams.forEach((teamName) => {
            const teamRegistrations = teamBuckets[teamName];
            if (teamRegistrations.length <= minTeamSize) return;

            const sortedByPlayerName = [...teamRegistrations].sort((a: any, b: any) => {
                const byName = String(a.playerName ?? '').localeCompare(
                    String(b.playerName ?? ''),
                    undefined,
                    { sensitivity: 'base' }
                );
                if (byName !== 0) return byName;
                const byCode = String(a.scoreCode ?? '').localeCompare(String(b.scoreCode ?? ''));
                if (byCode !== 0) return byCode;
                return String(a._id ?? '').localeCompare(String(b._id ?? ''));
            });

            newPlayableMap[teamName] = {};
            const teamSize = sortedByPlayerName.length;

            for (let hole = 1; hole <= totalHoles; hole += 1) {
                const startIndex = (hole - 1) % teamSize;
                const playableScoreCodes: string[] = [];

                for (let i = 0; i < minTeamSize; i += 1) {
                    const registrationIndex = (startIndex + i) % teamSize;
                    playableScoreCodes.push(sortedByPlayerName[registrationIndex].scoreCode);
                }

                newPlayableMap[teamName][hole] = playableScoreCodes;
            }
        });

        setShamblePlayableDriveMap(newPlayableMap);
    }, [eventData?.playFormat, eventData?.useTeams, eventData?.totalHoles, eventData?.rounds, eventData?.teamSize, registrationData]);

    const isShambleInPlay = useCallback((playerScoreCode: string, holeNumber: number) => {
        return !hasUnevenShambleTeams || !playerScoreCode || !holeNumber
            ? false
            : Object.values(shamblePlayableDriveMap).some((holeMap) =>
            Array.isArray(holeMap?.[holeNumber]) && holeMap[holeNumber].includes(playerScoreCode)
        );
    }, [hasUnevenShambleTeams, shamblePlayableDriveMap]);

    // Memoize the context value
    const memoizedValue = useMemo(() => ({
        eventData, setEventData,
        courseData, setCourseData,
        scoreData, setScoreData,
        ownEventScore, setOwnEventScore,
        scoreDataRef,
        registrationData, setRegistrationData,
        leaderboardData, setLeaderboardData,
        scoreCode, setScoreCode,
        currentRound, setCurrentRound,
        startHole, setStartHole,
        leaderboardLastQuery, setLeaderboardLastQuery,
        groupConfirmResult, setGroupConfirmResult,
        groupScorer, setGroupScorer,
        leaderboardCourseData, setLeaderboardCourseData,
        adminMode, setAdminMode,
        userData, setUserData,
        adminEvents, setAdminEvents,
        shamblePlayableDriveMap,
        shambleMinTeamSize,
        hasUnevenShambleTeams,
        isShambleInPlay
    }), [eventData, courseData, scoreData, ownEventScore, registrationData, leaderboardData, scoreCode,
         currentRound, startHole, leaderboardLastQuery, groupConfirmResult, groupScorer, leaderboardCourseData, adminMode, userData, adminEvents,
         shamblePlayableDriveMap, shambleMinTeamSize, hasUnevenShambleTeams, isShambleInPlay]);
    
    
    return (
        <DataContext.Provider value={memoizedValue}>
            {children}
        </DataContext.Provider>
    );
};

export const useDataContext = () => {
    const context = useContext(DataContext);

    if (!context) {
        throw new Error('useDataContext must be used within a DataProvider');
    }
    return context;
};